import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Anima números de 0 até um alvo quando a seção entra na viewport ("crescendo").
 *
 * Cada número-alvo é um descendente com `ref="value[]"` e os atributos:
 *   - `data-target`  número final (inteiro)
 *   - `data-prefix`  prefixo opcional (ex.: "+")
 *   - `data-suffix`  sufixo opcional (ex.: "%")
 * A duração total (ms) vem de `data-duration` no host (default 1600).
 *
 * O HTML já renderiza o valor final (bom para no-JS/SEO); este componente só
 * reseta para 0 quando a animação está habilitada. Respeita prefers-reduced-motion.
 *
 * @extends {Component}
 */
class CountUpComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #done = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.#values.length === 0) return;

    // Sem animação: deixa o valor final que já veio no HTML.
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
      this.#paint(1);
      return;
    }

    this.#paint(0);
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.#done) {
            this.#done = true;
            this.#observer?.disconnect();
            this.#run();
          }
        }
      },
      { threshold: 0.35 }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /** @returns {HTMLElement[]} */
  get #values() {
    const refs = this.refs.value;
    if (Array.isArray(refs)) return /** @type {HTMLElement[]} */ (refs);
    return refs ? [/** @type {HTMLElement} */ (refs)] : [];
  }

  #run() {
    const duration = Number(this.dataset.duration) || 1600;
    const start = performance.now();
    const items = this.#values.map((el) => ({ el, target: Number(el.dataset.target) || 0 }));

    /** @param {number} now */
    const frame = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      for (const { el, target } of items) this.#text(el, Math.round(target * eased));

      if (progress < 1) {
        requestAnimationFrame(frame);
      } else {
        for (const { el, target } of items) this.#text(el, target);
      }
    };

    requestAnimationFrame(frame);
  }

  /** @param {number} factor */
  #paint(factor) {
    for (const el of this.#values) {
      const target = Number(el.dataset.target) || 0;
      this.#text(el, Math.round(target * factor));
    }
  }

  /**
   * @param {HTMLElement} el
   * @param {number} value
   */
  #text(el, value) {
    el.textContent = `${el.dataset.prefix || ''}${value}${el.dataset.suffix || ''}`;
  }
}

if (!customElements.get('count-up')) {
  customElements.define('count-up', CountUpComponent);
}
